import { Box, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { fetchReceiptHistory } from "../api/auth";
import type { ReceiptSubmissionItem } from "../api/types";
import { useLocalization } from "../contexts/LocalizationContext";

const STATUS_COLOR: Record<string, string> = {
  PROCESSING: "#D5AD4C", PENDING_REVIEW: "#D5AD4C",
  AUTO_APPROVED: "#4A7A52", APPROVED: "#4A7A52",
  AUTO_REJECTED: "#C0392B", REJECTED: "#C0392B",
};

function formatDate(iso: string) {
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch { return iso; }
}

function ReceiptRow({ item, t }: { item: ReceiptSubmissionItem; t: (key: string) => string }) {
  const statusColor = STATUS_COLOR[item.status] ?? "#888";
  const statusLabel = (() => {
    if (item.status === "PROCESSING" || item.status === "PENDING_REVIEW") return t("receiptSubmissions.underReview");
    if (item.status === "AUTO_APPROVED" || item.status === "APPROVED") return t("receiptSubmissions.approved");
    if (item.status === "AUTO_REJECTED" || item.status === "REJECTED") return t("receiptSubmissions.rejected");
    return item.status;
  })();
  const isRejected = item.status === "AUTO_REJECTED" || item.status === "REJECTED";

  return (
    <Box sx={{
      display: "flex", alignItems: "center", minHeight: 69,
      borderRadius: "16px", border: "1px solid #EAEAEA", background: "#fff",
      px: 2, py: 1.5,
    }}>
      <Box sx={{ width: 40, height: 40, flexShrink: 0, borderRadius: "10px", background: "#F5F0EA", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
          <path d="M7 3.5h10a1 1 0 0 1 1 1V20l-2-1.2L14 20l-2-1.2L10 20l-2-1.2L6 20V4.5a1 1 0 0 1 1-1z" stroke="#9B7744" strokeWidth="1.5" strokeLinejoin="round"/>
          <path d="M9 8h6M9 11.5h6M9 15h4" stroke="#9B7744" strokeWidth="1.5" strokeLinecap="round"/>
        </svg>
      </Box>

      <Box sx={{ ml: 1.5, flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "#1E2939", lineHeight: 1.4, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {item.storeName ?? t("receiptSubmissions.receipt")}
        </Typography>
        <Typography sx={{ fontSize: "11px", color: "#99A1AF", lineHeight: 1.4 }}>
          {formatDate(item.createdAt)}
        </Typography>
        {isRejected && (
          <Typography sx={{ mt: "2px", fontSize: "11px", color: "#C0392B", lineHeight: 1.4 }}>
            {item.rejectionReason}
          </Typography>
        )}
      </Box>

      <Box sx={{ ml: 1.5, flexShrink: 0, textAlign: "right" }}>
        <Typography sx={{ fontSize: "13px", fontWeight: 700, color: statusColor, lineHeight: 1.4 }}>
          {statusLabel}
        </Typography>
        {item.pointsAwarded != null && (
          <Typography sx={{ fontSize: "11px", color: "#99A1AF", lineHeight: 1.4 }}>
            +{item.pointsAwarded} pts
          </Typography>
        )}
      </Box>
    </Box>
  );
}

export default function ReceiptSubmissionsPage() {
  const { t } = useLocalization();
  const navigate = useNavigate();

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["receiptHistory"],
    queryFn: () => fetchReceiptHistory(0, 50),
  });

  return (
    <Box sx={{ flex: 1, backgroundColor: "#F0EBE3", pb: '30px' }}>
      {/* Header */}
      <Box sx={{ backgroundColor: "#fff", borderBottom: "1px solid #EAEAEA", display: "flex", alignItems: "center", px: 2, pt: 2, pb: "14px", gap: 1.5, position: "sticky", top: 0, zIndex: 10 }}>
        <Box
          component="button"
          onClick={() => navigate(-1)}
          sx={{ width: 36, height: 36, border: "none", background: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "#3A3A3A", borderRadius: "50%", "&:active": { background: "#F0F0F0" } }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M19 12H5M12 19l-7-7 7-7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
        </Box>
        <Typography sx={{ fontSize: "17px", fontWeight: 700, color: "#1A1A1A" }}>
          {t("receiptSubmissions.title")}
        </Typography>
      </Box>

      <Box sx={{ px: 2, pt: 2, display: "flex", flexDirection: "column", gap: 1.25 }}>
        {isLoading ? (
          [0, 1, 2].map(i => (
            <Box key={i} sx={{ display: "flex", alignItems: "center", minHeight: 69, borderRadius: "16px", border: "1px solid #EAEAEA", background: "#fff", px: 2, py: 1.5, gap: 1.5 }}>
              <Box sx={{ width: 40, height: 40, borderRadius: "50%", background: "#E4DACB", flexShrink: 0 }} />
              <Box sx={{ flex: 1, height: 12, borderRadius: 99, background: "#E4DACB" }} />
              <Box sx={{ width: 60, height: 12, borderRadius: 99, background: "#E4DACB" }} />
            </Box>
          ))
        ) : isError ? (
          <Box sx={{ borderRadius: "16px", border: "1px solid #EAEAEA", background: "#fff", px: 2, py: 4, textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center", gap: 1.5 }}>
            <Typography sx={{ fontSize: "13px", color: "#6A5442" }}>{t("receiptSubmissions.loadError")}</Typography>
            <Box component="button" onClick={() => void refetch()} sx={{ border: "1px solid #8B6F47", borderRadius: 99, px: 2, py: 0.75, background: "none", color: "#8B6F47", fontSize: "11px", fontWeight: 600, fontFamily: "inherit", cursor: "pointer" }}>
              {t("receiptSubmissions.retry")}
            </Box>
          </Box>
        ) : !data?.content.length ? (
          <Box sx={{ borderRadius: "16px", border: "1px solid #EAEAEA", background: "#fff", px: 2, py: 4, textAlign: "center" }}>
            <Typography sx={{ fontSize: "13px", color: "#6A5442" }}>{t("receiptSubmissions.empty")}</Typography>
          </Box>
        ) : (
          data.content.map(item => <ReceiptRow key={item.id} item={item} t={t} />)
        )}
      </Box>
    </Box>
  );
}
